import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Task, MediaType } from '../../../types';
import { TaskIllustration } from './TaskIllustration';
import { useUploadManager } from '../../../contexts/UploadManagerContext';
import './TaskCard.css';
import './TaskCard.glass.css';

function renderTitleWithAccent(text: string, accentWord?: string): React.ReactNode {
  const w = accentWord?.trim();
  if (!w) return text;
  const idx = text.toLowerCase().indexOf(w.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <em>{text.slice(idx, idx + w.length)}</em>
      {text.slice(idx + w.length)}
    </>
  );
}

/* ── Task card state — drives button + atmosphere ── */
export type TaskCardState =
  | 'active'
  | 'urgent'
  | 'resume'
  | 'pending'
  | 'complete'
  | 'just-completed'
  | 'transition'
  | 'needs_redo';

export interface TaskCardProps {
  task: Task;
  state: TaskCardState;
  timeLeft?: string;
  onStart?: () => void;
  onResume?: () => void;
  /** Draft is being opened — a remote draft re-downloads its media, which is
      slow on the connections that produce one. Shows progress, blocks retaps. */
  resuming?: boolean;
  onNext?: () => void;
  onViewAll?: () => void;
  /** Fired when the just-completed timed sequence finishes */
  onSequenceDone?: () => void;
  /** Milestone message shown during just-completed sequence */
  milestone?: string;
  /** Submission receipt timestamp — shown during just-completed sequence */
  submittedAt?: Date;
  /** Addendum: shown on complete cards when task allows adding more */
  onAddendum?: () => void;
  /** Time remaining for addendum window — e.g. "23h left" */
  addendumTimeLeft?: string;
  /** Redo: shown when researcher has requested a redo */
  onRedo?: () => void;
  /** Researcher's reason for requesting the redo */
  redoReason?: string;
  /** Card is the currently focused/centered card in the carousel */
  isFocused?: boolean;
}

/* ── Colour maps matching prototype TYPE_COLORS ── */
const TYPE_CHIP: Record<string, string> = {
  audio:  'tc-chip-audio',
  photo:  'tc-chip-photo',
  video:  'tc-chip-video',
  text:   'tc-chip-text',
  screen: 'tc-chip-screen',
  choice: 'tc-chip-choice',
  signification: 'tc-chip-signification',
  'signification-tension': 'tc-chip-sig-tension',
  'signification-triad': 'tc-chip-sig-triad',
  'signification-stones': 'tc-chip-sig-stones',
};

/* ── Small utility icons (buttons, meta) ── */
const ICONS = {
  check: (color: string) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  ),
  checkAnimated: (color: string) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <polyline
        points="20,6 9,17 4,12"
        strokeDasharray="24"
        strokeDashoffset="24"
        style={{ animation: 'checkmark-draw 400ms ease-out 200ms forwards' }}
      />
    </svg>
  ),
  arrow: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  clock: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#A09890" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  wifi: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#A08870" strokeWidth="1.5" strokeLinecap="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M12 20h.01" />
    </svg>
  ),
  rotate: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#D4715A" strokeWidth="2" strokeLinecap="round">
      <polyline points="1,4 1,10 7,10" />
      <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
    </svg>
  ),
};

/* ── Confetti burst — plays once on just-completed ── */
const CONFETTI_COLORS = ['#D4715A', '#6E7A5A', '#7BBFAF', '#C2AC8E', '#E8A09A', '#8A9A9E'];

const ConfettiBurst: React.FC = () => {
  const pieces = useMemo(() =>
    Array.from({ length: 30 }, (_, i) => ({
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      startX: 40 + Math.random() * 20,
      drift: (Math.random() - 0.5) * 60,
      r: Math.random() * 360,
      size: 5 + Math.random() * 5,
      delay: Math.random() * 0.6,
      fallDistance: 60 + Math.random() * 80,
    })),
  []);

  return (
    <div className="tc-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="tc-confetti-piece"
          style={{
            left: `${p.startX}%`,
            background: p.color,
            '--size': `${p.size}px`,
            '--dx': `${p.drift}px`,
            '--fall': `${p.fallDistance}px`,
            '--r': `${p.r}deg`,
            '--delay': `${p.delay}s`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

/* ── Atmosphere hero — illustration with state overlays ── */
const Atmosphere: React.FC<{ type: string; state: TaskCardState; floating?: boolean }> = ({ type, state, floating }) => {
  const isComplete = state === 'complete' || state === 'just-completed';
  const atmClass =
    state === 'needs_redo' ? 'tc-atm-redo' :
    isComplete ? 'tc-atm-complete' :
    state === 'transition' ? 'tc-atm-transition' :
    '';

  return (
    <div className={`tc-atmosphere ${atmClass}`}>
      <TaskIllustration type={type as any} floating={floating} />
      {state === 'just-completed' && <ConfettiBurst />}
      {isComplete && (
        <>
          <div className="tc-burst-ring" />
          <div className="tc-burst-ring tc-burst-ring-2" />
        </>
      )}
      {state === 'transition' && <div className="tc-ripple" />}
    </div>
  );
};

/* ── Main TaskCard ── */
export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  state,
  timeLeft,
  onStart,
  onResume,
  resuming,
  onNext,
  onViewAll,
  onSequenceDone,
  milestone,
  submittedAt,
  onAddendum,
  addendumTimeLeft,
  onRedo,
  redoReason,
  isFocused,
}) => {
  const isChoice = task.mediaTypes.length > 1;
  const mediaType = isChoice ? 'choice' : (task.mediaTypes[0] || MediaType.Audio);
  // Resolve signification subtype for illustration and chip
  const illustrationType = mediaType === 'signification' && task.significationConfig?.items?.[0]?.mode
    ? `signification-${task.significationConfig.items[0].mode}`
    : mediaType;
  const chipClass = TYPE_CHIP[illustrationType] || TYPE_CHIP[mediaType] || 'tc-chip-audio';
  const isUrgent = state === 'urgent' || task.isUrgent;

  /* ── Voice assist — read task aloud ── */
  const { isOnline: uploadOnline } = useUploadManager();
  const [isSpeaking, setIsSpeaking] = useState(false);

  const toggleSpeak = useCallback(() => {
    if (isSpeaking) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const text = `${task.title || ''}. ${task.prompt || ''}`;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-IN';
    utterance.rate = 0.95;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  }, [isSpeaking, task.title, task.prompt]);

  // Stop speaking when card loses focus or task starts
  useEffect(() => {
    return () => { speechSynthesis.cancel(); };
  }, []);

  /* ── Just-completed timed sequence ── */
  const [submitPhase, setSubmitPhase] = useState<'submitted' | 'completed'>('submitted');
  const [showNextPill, setShowNextPill] = useState(false);
  const sequenceDoneRef = useRef(onSequenceDone);
  sequenceDoneRef.current = onSequenceDone;

  useEffect(() => {
    if (state !== 'just-completed') {
      setShowNextPill(false);
      return;
    }
    setSubmitPhase('submitted');
    setShowNextPill(true);

    const t1 = setTimeout(() => setSubmitPhase('completed'), 3000);
    const t2 = setTimeout(() => {
      setShowNextPill(false);
      sequenceDoneRef.current?.();
    }, 5000);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [state]);

  const handleNextPillClick = () => {
    setShowNextPill(false);
    onNext?.();
  };

  const renderButton = () => {
    switch (state) {
      case 'active':
      case 'urgent':
        return (
          <button className="tc-start-btn" onClick={onStart}>
            {ICONS.arrow()} Start task
          </button>
        );
      case 'resume':
        return (
          <button
            className="tc-resume-btn"
            onClick={onResume}
            disabled={resuming}
            aria-busy={resuming || undefined}
          >
            {ICONS.rotate()} {resuming ? 'Opening your draft…' : 'Resume task'}
          </button>
        );
      case 'just-completed':
        return (
          <>
            {milestone && <div className="tc-milestone">{milestone}</div>}
            <motion.button
              className="tc-done-btn"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              {ICONS.checkAnimated('#6E7A5A')} {submitPhase === 'submitted' ? 'Submitted \u2713' : 'Task completed'}
            </motion.button>
            {submittedAt && (
              <div className="tc-receipt">
                Submitted at {submittedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
            {showNextPill && (
              <button className="tc-next-pill" onClick={handleNextPillClick}>
                <span className="tc-next-pill-label">&rarr; Next task</span>
                <span className="tc-next-pill-bar" />
              </button>
            )}
          </>
        );
      case 'complete':
        return (
          <>
            <button className="tc-done-btn">
              {ICONS.check('#6E7A5A')} Task completed
            </button>
            {onAddendum && (
              <button className="tc-addendum-btn" onClick={onAddendum}>
                + Add more to your response
                {addendumTimeLeft && <span className="tc-addendum-time">{addendumTimeLeft}</span>}
              </button>
            )}
          </>
        );
      case 'pending':
        return (
          <button className="tc-pending-btn">
            {ICONS.wifi()} {uploadOnline ? 'Uploading your response\u2026' : 'Will upload when back online'}
          </button>
        );
      case 'needs_redo':
        return (
          <>
            {redoReason && (
              <div className="tc-redo-reason">
                <span className="tc-redo-reason-label">{ICONS.rotate()} Redo requested</span>
                <span className="tc-redo-reason-text">{redoReason}</span>
              </div>
            )}
            <button className="tc-redo-btn" onClick={onRedo}>
              {ICONS.rotate()} Redo this task
            </button>
          </>
        );
      case 'transition':
        return (
          <>
            <button className="tc-next-btn" onClick={onNext}>
              {ICONS.arrow()} Start next task
            </button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="tc-stage" data-state={state} data-format={mediaType}>
      <Atmosphere type={illustrationType} state={state} floating={isFocused} />
      <div className="tc-body">
        <div className="tc-meta">
          {isChoice ? (
            <span className="tc-chip tc-chip-choice">Choice</span>
          ) : (
            <span className={`tc-chip ${chipClass}`}>
              {mediaType === 'signification' && task.significationConfig?.items?.[0]?.mode
                ? ({ tension: 'tension', triad: 'triad', stones: 'grouping' }[task.significationConfig.items[0].mode] ?? task.significationConfig.items[0].mode)
                : mediaType}
            </span>
          )}
          {isUrgent && <span className="tc-chip tc-chip-urgent">Urgent</span>}
          <span className={`tc-chip ${isUrgent ? 'tc-chip-time-urgent' : 'tc-chip-time'}`}>
            {timeLeft || ''}
          </span>
        </div>
        {state === 'transition' && (
          <>
            <div className="tc-transition-label">Nice work!</div>
            <div className="tc-transition-sub">Ready for the next one?</div>
          </>
        )}
        {state !== 'transition' && (
          <>
            <div className="tc-title-row">
              <div className="tc-title">{renderTitleWithAccent(task.title || task.prompt, task.accentWord)}</div>
              {'speechSynthesis' in window && (
                <button
                  className={`tc-speak-btn ${isSpeaking ? 'tc-speak-btn--active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleSpeak(); }}
                  aria-label={isSpeaking ? 'Stop reading' : 'Read aloud'}
                >
                  {isSpeaking ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  )}
                </button>
              )}
            </div>
            {task.title && task.prompt && (
              <div className="tc-prompt">
                {task.prompt}
              </div>
            )}
          </>
        )}
        {renderButton()}
      </div>
    </div>
  );
};
